import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Box,
  Stack,
  Avatar,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import PageLayout from '../components/PageLayout';
import ChangePasswordForm from '../components/ChangePasswordForm';
import { useAuth } from '../contexts/AuthContext';
import { useClientProfile } from '../hooks/useClientProfile';

const Profile = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, updateProfile } = useAuth();
  const { clientProfile, loading: profileLoading, updateClientProfile } = useClientProfile();

  const [account, setAccount] = useState({ firstName: '', lastName: '', phoneNumber: '' });
  const [address, setAddress] = useState({ address: '', city: '', state: '', zipCode: '' });

  const [savingAccount, setSavingAccount] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [accountMessage, setAccountMessage] = useState(null); // { severity, text }
  const [addressMessage, setAddressMessage] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { state: { returnTo: '/profile' } });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user) {
      setAccount({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (clientProfile) {
      setAddress({
        address: clientProfile.address || '',
        city: clientProfile.city || '',
        state: clientProfile.state || '',
        zipCode: clientProfile.zipCode || ''
      });
    }
  }, [clientProfile]);

  const handleSaveAccount = async () => {
    setSavingAccount(true);
    setAccountMessage(null);
    try {
      await updateProfile(account);
      setAccountMessage({ severity: 'success', text: 'Account details saved.' });
    } catch (err) {
      setAccountMessage({
        severity: 'error',
        text: err.response?.data?.error || err.message || 'Failed to save account details.'
      });
    } finally {
      setSavingAccount(false);
    }
  };

  const handleSaveAddress = async () => {
    setSavingAddress(true);
    setAddressMessage(null);
    try {
      await updateClientProfile(address);
      setAddressMessage({ severity: 'success', text: 'Service address saved.' });
    } catch (err) {
      setAddressMessage({
        severity: 'error',
        text: err.message || 'Failed to save service address.'
      });
    } finally {
      setSavingAddress(false);
    }
  };

  if (authLoading || (!user && !authLoading)) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </PageLayout>
    );
  }

  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();
  const roleName = typeof user.role === 'object' ? user.role?.name : user.role;

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
          <Avatar sx={{ width: 64, height: 64, fontSize: 24, bgcolor: 'primary.main' }}>
            {initials}
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">
              My Profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        </Stack>

        {/* Account details */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Account Details
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {accountMessage && (
            <Alert
              severity={accountMessage.severity}
              sx={{ mb: 2 }}
              onClose={() => setAccountMessage(null)}
            >
              {accountMessage.text}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={account.firstName}
                onChange={(e) => setAccount((a) => ({ ...a, firstName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={account.lastName}
                onChange={(e) => setAccount((a) => ({ ...a, lastName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={account.phoneNumber}
                onChange={(e) => setAccount((a) => ({ ...a, phoneNumber: e.target.value }))}
                inputProps={{ inputMode: 'tel' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={user.email || ''}
                disabled
                helperText="To change your email, please contact us."
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              onClick={handleSaveAccount}
              disabled={savingAccount || !account.firstName.trim() || !account.lastName.trim()}
              startIcon={savingAccount ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            >
              {savingAccount ? 'Saving…' : 'Save Changes'}
            </Button>
          </Box>
        </Paper>

        {/* Password */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Password
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <ChangePasswordForm />
        </Paper>

        {/* Service address — customers only */}
        {roleName === 'CLIENT' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Service Address
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Used to pre-fill your address when booking a cleaning.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {addressMessage && (
            <Alert
              severity={addressMessage.severity}
              sx={{ mb: 2 }}
              onClose={() => setAddressMessage(null)}
            >
              {addressMessage.text}
            </Alert>
          )}

          {profileLoading && !clientProfile ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    value={address.address}
                    onChange={(e) => setAddress((a) => ({ ...a, address: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    label="City"
                    value={address.city}
                    onChange={(e) => setAddress((a) => ({ ...a, city: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="State"
                    value={address.state}
                    onChange={(e) => setAddress((a) => ({ ...a, state: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={address.zipCode}
                    onChange={(e) => setAddress((a) => ({ ...a, zipCode: e.target.value }))}
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  variant="contained"
                  onClick={handleSaveAddress}
                  disabled={savingAddress}
                  startIcon={savingAddress ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                >
                  {savingAddress ? 'Saving…' : 'Save Address'}
                </Button>
              </Box>
            </>
          )}
        </Paper>
        )}
      </Container>
    </PageLayout>
  );
};

export default Profile;
